import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Activity, 
  Clock, 
  Users, 
  Target, 
  TrendingUp, 
  Filter,
  Download,
  RefreshCw,
  EyeOff
} from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { useTrackingContext } from '../../contexts/TrackingContext';
import { useAuth } from '../../contexts/AuthContext';
import { lessonService } from '../../services/lessonService';
import { studentService } from '../../services/studentService';
import { Lesson, Student } from '../../types';
import { trackingService } from '../../services/trackingService';
interface TrackingDashboardPageProps {}

export const TrackingDashboardPage: React.FC<TrackingDashboardPageProps> = () => {
  const { t } = useTranslation();
  const { lessonId } = useParams<{ lessonId?: string }>();
  const { isTrackingEnabled } = useTrackingContext();
  const { user } = useAuth();

  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(lessonId || null);
  const [lessonSummary, setLessonSummary] = useState<any>(null);
  const [lessonEvents, setLessonEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState<'json' | 'csv' | 'pdf'>('json');
  const [isExporting, setIsExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    end: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000)
  });
  const [students, setStudents] = useState<Map<string, Student>>(new Map());
  const [filteredLessons, setFilteredLessons] = useState<Lesson[]>([]);
  const [lessonSearchTerm, setLessonSearchTerm] = useState('');
  const [showLessonDropdown, setShowLessonDropdown] = useState(false);

  // Load lessons and students
  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;
      
      try {
        const [fetchedLessons, fetchedStudents] = await Promise.all([
          lessonService.getAllLessons(user.id),
          studentService.getStudents(user.id)
        ]);
        setLessons(fetchedLessons);
        
        // Create a map of student IDs to student objects
        const studentMap = new Map<string, Student>();
        fetchedStudents.forEach(student => {
          studentMap.set(student.id, student);
        });
        setStudents(studentMap);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, [user?.id]);

  // Filter lessons based on search term
  useEffect(() => {
    const filtered = lessons.filter(lesson =>
      lesson.metadata?.title?.toLowerCase().includes(lessonSearchTerm.toLowerCase()) ||
      lesson.metadata?.subject?.toLowerCase().includes(lessonSearchTerm.toLowerCase())
    );
    setFilteredLessons(filtered);
  }, [lessons, lessonSearchTerm]);

  // Set initial lesson search term if lessonId is provided
  useEffect(() => {
    if (selectedLessonId && lessons.length > 0) {
      const selectedLesson = lessons.find(lesson => lesson.metadata?.lesson_id === selectedLessonId);
      if (selectedLesson) {
        setLessonSearchTerm(selectedLesson.metadata?.title || t('trackingDashboardPage.untitledLesson'));
      }
    }
  }, [selectedLessonId, lessons, t]);

  // Load tracking data for selected lesson
  useEffect(() => {
    const loadTrackingData = async () => {
      if (!selectedLessonId) {
        setLessonSummary(null);
        setLessonEvents([]);
        return;
      }

      setIsLoading(true);
      try {
        // Use the tracking service directly for this specific lesson
        const [summary, events] = await Promise.all([
          trackingService.getLessonSummary(selectedLessonId, dateRange.start, dateRange.end),
          trackingService.getLessonEvents(selectedLessonId, dateRange.start, dateRange.end)
        ]);
        setLessonSummary(summary);
        setLessonEvents(events);
      } catch (error) {
        console.error('Error loading tracking data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTrackingData();
  }, [selectedLessonId, dateRange]);

  const handleExport = async () => {
    if (!selectedLessonId) return;

    setIsExporting(true);
    try {
      const data = await trackingService.exportData(selectedLessonId, exportFormat);
      
      // Create and download file
      const blob = new Blob([data], { 
        type: exportFormat === 'json' ? 'application/json' : 
              exportFormat === 'csv' ? 'text/csv' : 'application/pdf' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tracking-data-${selectedLessonId}-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearData = async () => {
    if (!selectedLessonId) return;
    
    if (window.confirm(t('trackingDashboardPage.confirmClearData'))) {
      try {
        await trackingService.clearLessonData(selectedLessonId);
        setLessonSummary(null);
        setLessonEvents([]);
      } catch (error) {
        console.error('Error clearing lesson data:', error);
      }
    }
  };

  const filteredEvents = lessonEvents.filter(event => {
    try {
      const timestamp = event.timestamp;
      let eventDate: Date;
      
      if (timestamp instanceof Date) {
        eventDate = timestamp;
      } else if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
        // Firestore Timestamp
        eventDate = (timestamp as any).toDate();
      } else if (typeof timestamp === 'string') {
        eventDate = new Date(timestamp);
      } else if (typeof timestamp === 'number') {
        eventDate = new Date(timestamp);
      } else {
        return false; // Skip invalid timestamps
      }
      
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      return eventDate >= startDate && eventDate <= endDate;
    } catch (error) {
      return false; // Skip events with invalid timestamps
    }
  });

  const selectedLesson = lessons.find(lesson => lesson.metadata?.lesson_id === selectedLessonId);

  // Show loading state if user is not available
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('trackingDashboardPage.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{t('trackingDashboardPage.trackingDashboard')}</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">{t('trackingDashboardPage.monitorStudentEngagement')}</p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">{t('trackingDashboardPage.filters')}</span>
          </button>
          <button
            onClick={() => window.location.reload()}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('trackingDashboardPage.refreshData')}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('trackingDashboardPage.filters')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('trackingDashboardPage.lesson')}</label>
              <div className="relative">
                <input
                  type="text"
                  value={lessonSearchTerm}
                  onChange={(e) => {
                    setLessonSearchTerm(e.target.value);
                    setShowLessonDropdown(true);
                    if (!e.target.value) {
                      setSelectedLessonId(null);
                    }
                  }}
                  onFocus={() => {
                    setShowLessonDropdown(true);
                    if (!lessonSearchTerm) {
                      setFilteredLessons(lessons);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowLessonDropdown(false), 200);
                  }}
                  placeholder={t('trackingDashboardPage.searchLessons')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {filteredLessons.length > 0 && showLessonDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredLessons.map((lesson) => (
                      <button
                        key={lesson.metadata?.lesson_id}
                        type="button"
                        onClick={() => {
                          setSelectedLessonId(lesson.metadata?.lesson_id || null);
                          setLessonSearchTerm(lesson.metadata?.title || t('trackingDashboardPage.untitledLesson'));
                          setShowLessonDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium">{lesson.metadata?.title || t('trackingDashboardPage.untitledLesson')}</div>
                        <div className="text-sm text-gray-500">
                  {lesson.metadata?.subject} - {t('lesson.grade')} {lesson.metadata?.grade}
                  {lesson.className && ` • ${lesson.className}`}
                </div>
                      </button>
                    ))}
                  </div>
                )}
                {filteredLessons.length === 0 && lessonSearchTerm && showLessonDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                    <div className="px-3 py-2 text-sm text-gray-500">{t('trackingDashboardPage.noLessonsFound')}</div>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('trackingDashboardPage.startDate')}</label>
              <input
                type="date"
                value={dateRange.start.toISOString().split('T')[0]}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: new Date(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('trackingDashboardPage.endDate')}</label>
              <input
                type="date"
                value={dateRange.end.toISOString().split('T')[0]}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: new Date(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Lesson Selection */}
      {!selectedLessonId && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="text-center py-8">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            {lessons.length === 0 ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('trackingDashboardPage.noLessonsAvailable')}</h3>
                <p className="text-gray-600 mb-4">{t('trackingDashboardPage.createLessonsToTrack')}</p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('trackingDashboardPage.noLessonSelected')}</h3>
                <p className="text-gray-600 mb-4">{t('trackingDashboardPage.selectLessonToViewData')}</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tracking Data */}
      {selectedLessonId && (
        <>
          {/* Lesson Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                  {selectedLesson?.metadata?.title || t('trackingDashboardPage.untitledLesson')}
                </h2>
                <p className="text-sm text-gray-600 truncate">{t('trackingDashboardPage.lessonId')}: {selectedLessonId}</p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-shrink-0">
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as 'json' | 'csv' | 'pdf')}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                >
                  <option value="json">JSON</option>
                  <option value="csv">CSV</option>
                  <option value="pdf">PDF</option>
                </select>
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 text-sm"
                >
                  <Download className="w-4 h-4" />
                  <span>{isExporting ? t('trackingDashboardPage.exporting') : t('trackingDashboardPage.export')}</span>
                </button>
                <button
                  onClick={handleClearData}
                  className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  {t('trackingDashboardPage.clearData')}
                </button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          {lessonSummary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{t('trackingDashboardPage.totalStudents')}</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{lessonSummary.totalStudents}</p>
                  </div>
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{t('trackingDashboardPage.averageTime')}</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{Math.round(lessonSummary.averageTime)}s</p>
                  </div>
                  <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{t('trackingDashboardPage.averageScore')}</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{Math.round(lessonSummary.averageScore)}%</p>
                  </div>
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{t('trackingDashboardPage.completionRate')}</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{Math.round(lessonSummary.completionRate * 100)}%</p>
                  </div>
                  <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
                </div>
              </div>
            </div>
          )}

          {/* Recent Events */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t('trackingDashboardPage.recentEvents')} ({filteredEvents.length})</h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">{t('trackingDashboardPage.loadingEvents')}</p>
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="p-6 text-center text-gray-600">
                  {t('trackingDashboardPage.noEventsFound')}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredEvents.slice(0, 50).map((event, index) => (
                    <div key={event.id || index} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                          <div>
                            <p className="font-medium text-gray-900">{event.eventType}</p>
                            <p className="text-sm text-gray-600">
                              {t('trackingDashboardPage.student')}: {students.get(event.studentId)?.name || event.studentId}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">
                            {(() => {
                              try {
                                const timestamp = event.timestamp;
                                if (timestamp instanceof Date) {
                                  return timestamp.toLocaleString();
                                } else if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
                                  // Firestore Timestamp
                                  return (timestamp as any).toDate().toLocaleString();
                                } else if (typeof timestamp === 'string') {
                                  return new Date(timestamp).toLocaleString();
                                } else if (typeof timestamp === 'number') {
                                  return new Date(timestamp).toLocaleString();
                                } else {
                                  return t('trackingDashboardPage.unknownTime');
                                }
                              } catch (error) {
                                return t('trackingDashboardPage.invalidTime');
                              }
                            })()}
                          </p>
                          {event.data && Object.keys(event.data).length > 0 && (
                            <p className="text-xs text-gray-500">
                              {Object.keys(event.data).length} {t('trackingDashboardPage.dataPoints')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* No Tracking Enabled */}
      {!isTrackingEnabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <EyeOff className="w-6 h-6 text-yellow-600" />
            <div>
              <h3 className="text-lg font-medium text-yellow-900">{t('trackingDashboardPage.trackingDisabled')}</h3>
              <p className="text-yellow-700">{t('trackingDashboardPage.enableTrackingMessage')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
